import React, { useState, useEffect } from 'react';
import { db, functions } from '../config/firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Send, Users, Image, Check, AlertTriangle, RefreshCw } from 'react-feather';
import '../components/styles/promotionalEmail.css';

const PromotionalEmail = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('default');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailContent, setEmailContent] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);

  // Templates for different promotional emails
  const templates = {
    default: {
      subject: 'Special Offer from Lorren\'s Makeup',
      content: 'Hello,\n\nWe have a special offer just for you!\n\nBest regards,\nLorren\'s Makeup Team',
      imageUrl: ''
    },
    seasonal: {
      subject: 'Seasonal Beauty Trends',
      content: 'Hello,\n\nCheck out our seasonal beauty trends and get ready for the new season!\n\nBest regards,\nLorren\'s Makeup Team',
      imageUrl: ''
    },
    discount: {
      subject: 'Exclusive Discount Inside!',
      content: 'Hello,\n\nEnjoy an exclusive discount on our services!\n\nUse code: SPECIAL20 for 20% off your next appointment.\n\nBest regards,\nLorren\'s Makeup Team',
      imageUrl: ''
    }
  };

  // Fetch subscribers on component mount
  useEffect(() => {
    fetchSubscribers();
  }, []);

  // Load template when selected
  useEffect(() => {
    if (selectedTemplate) {
      const template = templates[selectedTemplate];
      setEmailSubject(template.subject);
      setEmailContent(template.content);
      setImageUrl(template.imageUrl);
    }
  }, [selectedTemplate]);

  // Fetch all active newsletter subscribers
  const fetchSubscribers = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'newsletter_subscribers'),
        where('isActive', '==', true)
      );
      const snapshot = await getDocs(q);
      const subscriberList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSubscribers(subscriberList);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
      setStatus({
        type: 'error',
        message: 'Failed to load subscribers'
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission to send promotional email
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!emailSubject.trim() || !emailContent.trim()) {
      setStatus({
        type: 'error',
        message: 'Please enter both subject and content'
      });
      return;
    }
    
    setLoading(true);
    setStatus(null);
    
    try {
      // Call Firebase function to send promotional email
      const sendPromotionalEmail = httpsCallable(functions, 'sendPromotionalEmail');
      
      await sendPromotionalEmail({
        subject: emailSubject,
        content: emailContent,
        imageUrl: imageUrl,
        recipientCount: subscribers.length
      });
      
      setStatus({
        type: 'success',
        message: `Email sent successfully to ${subscribers.length} subscribers!`
      });
    } catch (error) {
      console.error('Error sending promotional email:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Failed to send email. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="promotional-email-container">
      <div className="promotional-email-header">
        <h2>Send Promotional Email</h2>
        <div className="subscriber-count">
          <Users size={16} />
          <span>{subscribers.length} Active Subscribers</span>
        </div>
      </div>
      
      <div className="promotional-email-content">
        <div className="template-section">
          <h3>Choose a Template</h3>
          <div className="template-grid">
            {Object.keys(templates).map(key => (
              <div 
                key={key}
                className={`template-card ${selectedTemplate === key ? 'selected' : ''}`}
                onClick={() => setSelectedTemplate(key)}
              >
                <h4>{key.charAt(0).toUpperCase() + key.slice(1)}</h4>
                <p>{templates[key].subject}</p>
              </div>
            ))}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="email-form">
          <div className="form-group">
            <label>Email Subject</label>
            <input
              type="text"
              value={emailSubject}
              onChange={(e) => setEmailSubject(e.target.value)}
              placeholder="Enter email subject"
              required
            />
          </div>
          
          <div className="form-group">
            <label>Image URL (Optional)</label>
            <div className="image-url-input">
              <input
                type="url"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              <Image size={20} />
            </div>
            <p className="input-hint">Add a URL to an image for your promotional email</p>
          </div>
          
          {imageUrl && (
            <div className="image-preview">
              <h4>Image Preview</h4>
              <img 
                src={imageUrl} 
                alt="Preview" 
                onError={(e) => e.target.style.display = 'none'} 
              />
            </div>
          )}
          
          <div className="form-group">
            <label>Email Content</label>
            <textarea
              value={emailContent}
              onChange={(e) => setEmailContent(e.target.value)}
              placeholder="Write your email content here..."
              rows="8"
              required
            ></textarea>
          </div>
          
          {status && (
            <div className={`status-message ${status.type}`}>
              {status.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
              <span>{status.message}</span>
            </div>
          )}
          
          <button 
            type="submit" 
            className="send-button"
            disabled={loading || subscribers.length === 0}
          >
            {loading ? (
              <>
                <RefreshCw size={16} className="spinning" />
                <span>Sending...</span>
              </>
            ) : (
              <>
                <Send size={16} />
                <span>Send to {subscribers.length} Subscribers</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default PromotionalEmail;